// src/solver/CircuitSolver.ts
import { DEFAULT_SIMULATION_OPTIONS, SIMULATION } from "@circuit-crafter/shared";

// src/solver/CircuitGraph.ts
var CircuitGraph = class {
  nodes = /* @__PURE__ */ new Map();
  edges = /* @__PURE__ */ new Map();
  nodesByTerminal = /* @__PURE__ */ new Map();
  constructor(components, wires) {
    this.buildGraph(components, wires);
  }
  buildGraph(components, wires) {
    for (const component of components) {
      for (const terminal of component.terminals) {
        const nodeId = `node-${terminal.id}`;
        this.nodes.set(nodeId, {
          id: nodeId,
          componentId: component.id,
          terminalId: terminal.id,
          terminalType: terminal.type,
          voltage: 0,
          connectedNodes: []
        });
        this.nodesByTerminal.set(terminal.id, nodeId);
      }
      if (component.terminals.length === 2) {
        const [terminal1, terminal2] = component.terminals;
        const node1Id = this.nodesByTerminal.get(terminal1.id);
        const node2Id = this.nodesByTerminal.get(terminal2.id);
        const resistance = this.getComponentResistance(component);
        const edgeId = `edge-${component.id}`;
        this.edges.set(edgeId, {
          id: edgeId,
          sourceNodeId: node1Id,
          targetNodeId: node2Id,
          componentId: component.id,
          componentType: component.type,
          resistance,
          current: 0
        });
        const node1 = this.nodes.get(node1Id);
        const node2 = this.nodes.get(node2Id);
        node1.connectedNodes.push(node2Id);
        node2.connectedNodes.push(node1Id);
      }
    }
    for (const wire of wires) {
      const sourceNodeId = this.nodesByTerminal.get(wire.fromTerminal);
      const targetNodeId = this.nodesByTerminal.get(wire.toTerminal);
      if (sourceNodeId && targetNodeId) {
        const edgeId = `wire-${wire.id}`;
        this.edges.set(edgeId, {
          id: edgeId,
          sourceNodeId,
          targetNodeId,
          wireId: wire.id,
          resistance: 1e-3,
          // Small resistance for wires
          current: 0
        });
        const sourceNode = this.nodes.get(sourceNodeId);
        const targetNode = this.nodes.get(targetNodeId);
        sourceNode.connectedNodes.push(targetNodeId);
        targetNode.connectedNodes.push(sourceNodeId);
      }
    }
  }
  getComponentResistance(component) {
    switch (component.type) {
      case "battery":
        return 1e-3;
      // Internal resistance
      case "resistor":
        return component.properties.resistance || 1e3;
      case "led":
        return 100;
      // Simplified LED resistance
      case "switch":
        return component.properties.isOpen ? Infinity : 1e-3;
      case "wire":
        return 1e-3;
      case "ground":
        return 1e-3;
      default:
        return 1e3;
    }
  }
  getNode(nodeId) {
    return this.nodes.get(nodeId);
  }
  getEdge(edgeId) {
    return this.edges.get(edgeId);
  }
  getNodeByTerminal(terminalId) {
    const nodeId = this.nodesByTerminal.get(terminalId);
    return nodeId ? this.nodes.get(nodeId) : void 0;
  }
  findPath(fromNodeId, toNodeId) {
    const visited = /* @__PURE__ */ new Set();
    const queue = [{ nodeId: fromNodeId, path: [fromNodeId] }];
    while (queue.length > 0) {
      const { nodeId, path } = queue.shift();
      if (nodeId === toNodeId) {
        return path;
      }
      if (visited.has(nodeId)) {
        continue;
      }
      visited.add(nodeId);
      const node = this.nodes.get(nodeId);
      if (node) {
        for (const connectedNodeId of node.connectedNodes) {
          if (!visited.has(connectedNodeId)) {
            queue.push({ nodeId: connectedNodeId, path: [...path, connectedNodeId] });
          }
        }
      }
    }
    return null;
  }
  findAllPaths(fromNodeId, toNodeId) {
    const paths = [];
    const visited = /* @__PURE__ */ new Set();
    const dfs = (currentId, path) => {
      if (currentId === toNodeId) {
        paths.push([...path]);
        return;
      }
      visited.add(currentId);
      const node = this.nodes.get(currentId);
      if (node) {
        for (const connectedNodeId of node.connectedNodes) {
          if (!visited.has(connectedNodeId)) {
            dfs(connectedNodeId, [...path, connectedNodeId]);
          }
        }
      }
      visited.delete(currentId);
    };
    dfs(fromNodeId, [fromNodeId]);
    return paths;
  }
};

// src/solver/CircuitSolver.ts
var CircuitSolver = class {
  _options;
  constructor(options = {}) {
    this._options = { ...DEFAULT_SIMULATION_OPTIONS, ...options };
  }
  get maxIterations() {
    return this._options.maxIterations;
  }
  solve(components, wires) {
    const warnings = [];
    const componentResults = [];
    const wireResults = [];
    const graph = new CircuitGraph(components, wires);
    const batteries = components.filter((c) => c.type === "battery");
    const grounds = components.filter((c) => c.type === "ground");
    if (batteries.length === 0) {
      warnings.push({
        type: "open_circuit",
        message: "No battery found - your circuit needs power!",
        componentIds: [],
        severity: "warning",
        helpText: "Drag a battery from the parts menu onto your board. Connect the + side to your circuit parts."
      });
    }
    if (grounds.length === 0 && batteries.length > 0) {
      warnings.push({
        type: "no_ground",
        message: "Add a ground to complete the circuit!",
        componentIds: [],
        severity: "info",
        helpText: "Ground is where electricity returns to. Connect it to the - side of your battery."
      });
    }
    this.detectDisconnectedComponents(components, graph, warnings);
    this.detectLEDReversePolarity(components, graph, warnings);
    let hasShortCircuit = false;
    let hasOpenCircuit = batteries.length === 0;
    let totalPower = 0;
    for (const battery of batteries) {
      const batteryVoltage = battery.properties.voltage;
      const positiveTerminal = battery.terminals.find((t) => t.type === "positive");
      const negativeTerminal = battery.terminals.find((t) => t.type === "negative");
      if (!positiveTerminal || !negativeTerminal) continue;
      const positiveNode = graph.getNodeByTerminal(positiveTerminal.id);
      const negativeNode = graph.getNodeByTerminal(negativeTerminal.id);
      if (!positiveNode || !negativeNode) continue;
      positiveNode.voltage = batteryVoltage;
      negativeNode.voltage = 0;
      const paths = graph.findAllPaths(positiveNode.id, negativeNode.id);
      if (paths.length === 0) {
        hasOpenCircuit = true;
        warnings.push({
          type: "open_circuit",
          message: "Electricity can't flow! Make a complete circle.",
          componentIds: [battery.id],
          severity: "warning",
          helpText: "Connect wires from the battery + side, through your parts, and back to the - side or ground. Electricity needs a complete path!"
        });
      }
      for (const path of paths) {
        let totalResistance = 0;
        for (let i = 0; i < path.length - 1; i++) {
          const nodeId = path[i];
          const nextNodeId = path[i + 1];
          for (const edge of graph.edges.values()) {
            if (edge.sourceNodeId === nodeId && edge.targetNodeId === nextNodeId || edge.sourceNodeId === nextNodeId && edge.targetNodeId === nodeId) {
              if (edge.resistance === Infinity) {
                totalResistance = Infinity;
                break;
              }
              totalResistance += edge.resistance;
              break;
            }
          }
          if (totalResistance === Infinity) break;
        }
        if (totalResistance < SIMULATION.MIN_RESISTANCE) {
          hasShortCircuit = true;
          warnings.push({
            type: "short_circuit",
            message: "Danger! Short circuit - too much power flowing!",
            componentIds: [battery.id],
            severity: "error",
            helpText: "You connected + to - with no parts in between! This is like a superhighway for electricity and can break things. Add a resistor or LED to slow it down."
          });
          totalResistance = SIMULATION.MIN_RESISTANCE;
        }
        const current = totalResistance === Infinity ? 0 : batteryVoltage / totalResistance;
        for (let i = 0; i < path.length - 1; i++) {
          const nodeId = path[i];
          const nextNodeId = path[i + 1];
          for (const edge of graph.edges.values()) {
            if (edge.sourceNodeId === nodeId && edge.targetNodeId === nextNodeId || edge.sourceNodeId === nextNodeId && edge.targetNodeId === nodeId) {
              edge.current += current;
              break;
            }
          }
        }
        let cumulativeResistance = 0;
        for (let i = 1; i < path.length - 1; i++) {
          const prevNodeId = path[i - 1];
          const nodeId = path[i];
          for (const edge of graph.edges.values()) {
            if (edge.sourceNodeId === prevNodeId && edge.targetNodeId === nodeId || edge.sourceNodeId === nodeId && edge.targetNodeId === prevNodeId) {
              cumulativeResistance += edge.resistance === Infinity ? 0 : edge.resistance;
              break;
            }
          }
          const node = graph.getNode(nodeId);
          if (node && totalResistance !== Infinity && totalResistance > 0) {
            const voltageDrop = cumulativeResistance / totalResistance * batteryVoltage;
            node.voltage = batteryVoltage - voltageDrop;
          }
        }
      }
    }
    for (const component of components) {
      const result = this.calculateComponentResult(component, graph);
      componentResults.push(result);
      if (result.isOverloaded) {
        const componentName = this.getComponentDisplayName(component.type);
        warnings.push({
          type: "overload",
          message: `${componentName} is overloaded`,
          componentIds: [component.id],
          severity: "warning",
          helpText: `Too much current or voltage is passing through this ${componentName.toLowerCase()}. Add resistors to limit current, or reduce the voltage source.`
        });
      }
      totalPower += result.power;
    }
    for (const wire of wires) {
      const wireResult = this.calculateWireResult(wire, graph);
      wireResults.push(wireResult);
    }
    return {
      success: !hasShortCircuit,
      components: componentResults,
      wires: wireResults,
      warnings,
      totalPower,
      hasShortCircuit,
      hasOpenCircuit,
      timestamp: Date.now()
    };
  }
  calculateComponentResult(component, graph) {
    let voltage = 0;
    let current = 0;
    let power = 0;
    let isOverloaded = false;
    let brightness;
    let logicState;
    let isActive;
    let isBlown;
    let effectiveResistance;
    const terminalNodes = component.terminals.map((t) => graph.getNodeByTerminal(t.id)).filter(Boolean);
    if (terminalNodes.length >= 2) {
      const node1 = terminalNodes[0];
      const node2 = terminalNodes[1];
      voltage = Math.abs(node1.voltage - node2.voltage);
      for (const edge of graph.edges.values()) {
        if (edge.componentId === component.id) {
          current = edge.current;
          break;
        }
      }
    }
    power = voltage * current;
    switch (component.type) {
      case "battery":
        power = Math.abs(power);
        break;
      case "resistor":
        const maxPower = 0.25;
        isOverloaded = power > maxPower * SIMULATION.OVERLOAD_FACTOR;
        break;
      case "led":
        const maxCurrent = component.properties.maxCurrent || 0.02;
        isOverloaded = current > maxCurrent * SIMULATION.OVERLOAD_FACTOR;
        brightness = Math.min(1, current / maxCurrent);
        break;
      case "switch":
        power = 0;
        break;
      case "and_gate":
      case "or_gate":
      case "not_gate":
        logicState = this.evaluateLogicGate(component, graph);
        break;
      case "capacitor":
        isActive = current > 1e-4;
        break;
      case "diode":
        const diodeForwardVoltage = component.properties.forwardVoltage || 0.7;
        if (terminalNodes.length >= 2) {
          const anodeNode = terminalNodes[0];
          const cathodeNode = terminalNodes[1];
          const diodeVoltage = anodeNode.voltage - cathodeNode.voltage;
          isActive = diodeVoltage >= diodeForwardVoltage;
          if (!isActive) {
            current = 0;
          }
        }
        break;
      case "transistor":
        const baseTerminal = component.terminals.find((t) => t.type === "base");
        if (baseTerminal) {
          const baseNode = graph.getNodeByTerminal(baseTerminal.id);
          const baseVoltage = baseNode?.voltage || 0;
          isActive = baseVoltage >= 0.7;
          if (!isActive) {
            current = 0;
          }
        }
        break;
      case "buzzer":
        const buzzerForwardVoltage = component.properties.forwardVoltage || 3;
        isActive = voltage >= buzzerForwardVoltage && current > 1e-3;
        isOverloaded = voltage > buzzerForwardVoltage * SIMULATION.OVERLOAD_FACTOR;
        break;
      case "motor":
        const motorRatedVoltage = component.properties.ratedVoltage || 6;
        const motorRatedCurrent = component.properties.ratedCurrent || 0.5;
        isActive = current > 0.01;
        isOverloaded = current > motorRatedCurrent * SIMULATION.OVERLOAD_FACTOR || voltage > motorRatedVoltage * SIMULATION.OVERLOAD_FACTOR;
        brightness = Math.min(1, voltage / motorRatedVoltage);
        break;
      case "potentiometer":
        const potMaxResistance = component.properties.maxResistance || 1e4;
        const potPosition = component.properties.position || 50;
        effectiveResistance = potPosition / 100 * potMaxResistance;
        isActive = current > 1e-4;
        break;
      case "fuse":
        const fuseRating = component.properties.rating || 1;
        isBlown = component.properties.isBlown || current > fuseRating;
        isActive = !isBlown && current > 1e-4;
        if (isBlown) {
          current = 0;
        }
        isOverloaded = current > fuseRating * 0.8;
        break;
    }
    let state = "normal";
    if (isBlown) {
      state = "blown";
    } else if (isOverloaded) {
      state = "overloaded";
    } else if (component.type === "switch") {
      state = component.properties.isOpen ? "open" : "closed";
    } else if (isActive !== void 0) {
      state = isActive ? "active" : "off";
    } else if (current > 1e-4) {
      state = "powered";
    } else {
      state = "off";
    }
    return {
      componentId: component.id,
      state,
      voltage,
      current,
      power,
      isOverloaded,
      brightness,
      logicState,
      isActive,
      isBlown,
      effectiveResistance
    };
  }
  calculateWireResult(wire, graph) {
    let current = 0;
    let voltage = 0;
    for (const edge of graph.edges.values()) {
      if (edge.wireId === wire.id) {
        current = edge.current;
        const sourceNode = graph.getNode(edge.sourceNodeId);
        const targetNode = graph.getNode(edge.targetNodeId);
        if (sourceNode && targetNode) {
          voltage = Math.abs(sourceNode.voltage - targetNode.voltage);
        }
        break;
      }
    }
    return {
      wireId: wire.id,
      current,
      voltage,
      isCarryingCurrent: current > 1e-4
    };
  }
  evaluateLogicGate(component, graph) {
    const inputTerminals = component.terminals.filter(
      (t) => t.type === "input" || t.type === "input_a" || t.type === "input_b"
    );
    const inputs = inputTerminals.map((terminal) => {
      const node = graph.getNodeByTerminal(terminal.id);
      return node ? node.voltage >= SIMULATION.LOGIC_THRESHOLD_VOLTAGE : false;
    });
    switch (component.type) {
      case "and_gate":
        return inputs.every(Boolean);
      case "or_gate":
        return inputs.some(Boolean);
      case "not_gate":
        return !inputs[0];
      default:
        return false;
    }
  }
  getComponentDisplayName(type) {
    const names = {
      battery: "Battery",
      resistor: "Resistor",
      led: "LED",
      switch: "Switch",
      and_gate: "AND Gate",
      or_gate: "OR Gate",
      not_gate: "NOT Gate",
      ground: "Ground",
      capacitor: "Capacitor",
      diode: "Diode",
      transistor: "Transistor",
      buzzer: "Buzzer",
      motor: "Motor",
      potentiometer: "Potentiometer",
      fuse: "Fuse"
    };
    return names[type] || type;
  }
  detectDisconnectedComponents(components, graph, warnings) {
    const batteries = components.filter((c) => c.type === "battery");
    if (batteries.length === 0) return;
    for (const component of components) {
      if (component.type === "battery" || component.type === "ground") continue;
      const hasWireConnection = component.terminals.some((terminal) => {
        const node = graph.getNodeByTerminal(terminal.id);
        return node && node.connectedNodes.length > 1;
      });
      if (!hasWireConnection) {
        const componentName = this.getComponentDisplayName(component.type);
        warnings.push({
          type: "disconnected",
          message: `${componentName} is not connected to the circuit`,
          componentIds: [component.id],
          severity: "info",
          helpText: `This ${componentName.toLowerCase()} has no wire connections. Connect its terminals to other components using wires to make it part of the circuit.`
        });
      }
    }
  }
  detectLEDReversePolarity(components, graph, warnings) {
    const leds = components.filter((c) => c.type === "led");
    for (const led of leds) {
      const positiveTerminal = led.terminals.find((t) => t.type === "positive");
      const negativeTerminal = led.terminals.find((t) => t.type === "negative");
      if (!positiveTerminal || !negativeTerminal) continue;
      const positiveNode = graph.getNodeByTerminal(positiveTerminal.id);
      const negativeNode = graph.getNodeByTerminal(negativeTerminal.id);
      if (!positiveNode || !negativeNode) continue;
      if (positiveNode.voltage < negativeNode.voltage && Math.abs(positiveNode.voltage - negativeNode.voltage) > 0.1) {
        warnings.push({
          type: "reverse_polarity",
          message: "LED is backwards! It won't light up.",
          componentIds: [led.id],
          severity: "warning",
          helpText: "LEDs only work one way! The + side should connect towards the battery +, and the - side should connect towards ground. Try spinning it around (press R)."
        });
      }
    }
  }
  /**
   * Run transient simulation to capture time-varying behavior
   * Useful for oscilloscope visualization, especially for capacitors and inductors
   */
  solveTransient(components, wires, duration, timeStep) {
    const traces = [];
    const numSteps = Math.floor(duration / timeStep);
    const measurableComponents = components.filter(
      (c) => c.type !== "wire" && c.type !== "ground"
    );
    for (const component of measurableComponents) {
      traces.push({
        componentId: component.id,
        data: []
      });
    }
    const capacitorCharges = /* @__PURE__ */ new Map();
    components.filter((c) => c.type === "capacitor").forEach((c) => capacitorCharges.set(c.id, 0));
    for (let step = 0; step <= numSteps; step++) {
      const currentTime = step * timeStep;
      const componentsWithCapacitorState = components.map((c) => {
        if (c.type === "capacitor") {
          const charge = capacitorCharges.get(c.id) || 0;
          const capacitance = c.properties.capacitance || 1e-4;
          const voltage = charge / capacitance;
          return {
            ...c,
            // Store voltage in a way the solver can use
            _transientVoltage: voltage
          };
        }
        return c;
      });
      const result = this.solve(componentsWithCapacitorState, wires);
      for (const componentResult of result.components) {
        const trace = traces.find((t) => t.componentId === componentResult.componentId);
        if (trace) {
          trace.data.push({
            time: currentTime,
            voltage: componentResult.voltage,
            current: componentResult.current
          });
        }
        const component = components.find((c) => c.id === componentResult.componentId);
        if (component?.type === "capacitor") {
          const oldCharge = capacitorCharges.get(component.id) || 0;
          const newCharge = oldCharge + componentResult.current * timeStep;
          const dampedCharge = newCharge * 0.99;
          capacitorCharges.set(component.id, dampedCharge);
        }
      }
    }
    return {
      traces,
      currentTime: duration,
      duration,
      timeStep
    };
  }
};

// src/solver/ChallengeValidator.ts
import { RATING_THRESHOLDS } from "@circuit-crafter/shared";
var ChallengeValidator = class {
  solver;
  constructor() {
    this.solver = new CircuitSolver();
  }
  validate(components, wires, challenge, solveTimeSeconds) {
    const feedback = [];
    const constraintsPassed = this.validateConstraints(
      components,
      wires,
      challenge.constraints,
      feedback
    );
    const simulation = this.solver.solve(components, wires);
    const targetPassed = this.validateTarget(
      simulation,
      components,
      challenge.target,
      feedback
    );
    const passed = constraintsPassed && targetPassed && simulation.success;
    let rating;
    let timeBonusApplied = false;
    if (passed && challenge.timeLimitSeconds) {
      const timeRatio = solveTimeSeconds / challenge.timeLimitSeconds;
      if (timeRatio <= RATING_THRESHOLDS.gold) {
        rating = "gold";
        feedback.push("Excellent! Gold rating achieved!");
        timeBonusApplied = true;
      } else if (timeRatio <= RATING_THRESHOLDS.silver) {
        rating = "silver";
        feedback.push("Great job! Silver rating achieved!");
        timeBonusApplied = true;
      } else if (timeRatio <= RATING_THRESHOLDS.bronze) {
        rating = "bronze";
        feedback.push("Good work! Bronze rating achieved!");
      }
    } else if (passed) {
      rating = "bronze";
      feedback.push("Challenge completed!");
    }
    for (const warning of simulation.warnings) {
      if (warning.severity === "error") {
        feedback.push(`Error: ${warning.message}`);
      } else if (warning.severity === "warning") {
        feedback.push(`Warning: ${warning.message}`);
      }
    }
    return {
      passed,
      rating,
      feedback,
      constraintsPassed,
      targetPassed,
      timeBonusApplied
    };
  }
  validateConstraints(components, wires, constraints, feedback) {
    if (!constraints) return true;
    let allPassed = true;
    if (constraints.maxComponents !== void 0) {
      const componentCount = components.filter((c) => c.type !== "wire").length;
      if (componentCount > constraints.maxComponents) {
        feedback.push(
          `Too many components: ${componentCount}/${constraints.maxComponents}`
        );
        allPassed = false;
      }
    }
    if (constraints.maxWires !== void 0) {
      if (wires.length > constraints.maxWires) {
        feedback.push(`Too many wires: ${wires.length}/${constraints.maxWires}`);
        allPassed = false;
      }
    }
    if (constraints.allowedComponentTypes) {
      for (const component of components) {
        if (!constraints.allowedComponentTypes.includes(component.type)) {
          feedback.push(`Component type "${component.type}" is not allowed`);
          allPassed = false;
        }
      }
    }
    if (constraints.forbiddenComponentTypes) {
      for (const component of components) {
        if (constraints.forbiddenComponentTypes.includes(component.type)) {
          feedback.push(`Component type "${component.type}" is forbidden`);
          allPassed = false;
        }
      }
    }
    return allPassed;
  }
  validateTarget(simulation, components, target, feedback) {
    switch (target.type) {
      case "power_led":
        return this.validatePowerLED(simulation, components, target, feedback);
      case "logic_output":
        return this.validateLogicOutput(simulation, target, feedback);
      case "voltage_match":
        return this.validateVoltageMatch(simulation, target, feedback);
      case "custom":
        return this.validateCustomTarget(simulation, target, feedback);
      default:
        feedback.push("Unknown target type");
        return false;
    }
  }
  validatePowerLED(simulation, components, target, feedback) {
    const leds = components.filter((c) => c.type === "led");
    const targetLeds = target.targetComponents ? leds.filter((led) => target.targetComponents.includes(led.id)) : leds;
    if (targetLeds.length === 0) {
      feedback.push("No target LEDs found");
      return false;
    }
    let allPowered = true;
    for (const led of targetLeds) {
      const ledResult = simulation.components.find((r) => r.componentId === led.id);
      if (!ledResult) {
        feedback.push(`LED ${led.id} not found in simulation`);
        allPowered = false;
        continue;
      }
      if (ledResult.state !== "powered" || (ledResult.brightness ?? 0) < 0.1) {
        feedback.push(`LED is not properly powered`);
        allPowered = false;
      } else if (ledResult.isOverloaded) {
        feedback.push(`LED is overloaded! Reduce current.`);
        allPowered = false;
      }
    }
    if (allPowered) {
      feedback.push("All target LEDs are properly powered!");
    }
    return allPowered;
  }
  validateLogicOutput(simulation, target, feedback) {
    if (!target.expectedOutputs) {
      feedback.push("No expected outputs defined");
      return false;
    }
    let allMatch = true;
    for (const [componentId, expectedValue] of Object.entries(target.expectedOutputs)) {
      const result = simulation.components.find((r) => r.componentId === componentId);
      if (!result) {
        feedback.push(`Component ${componentId} not found`);
        allMatch = false;
        continue;
      }
      if (typeof expectedValue === "boolean") {
        if (result.logicState !== expectedValue) {
          feedback.push(`Logic gate output mismatch`);
          allMatch = false;
        }
      }
    }
    if (allMatch) {
      feedback.push("All logic outputs match expected values!");
    }
    return allMatch;
  }
  validateVoltageMatch(simulation, target, feedback) {
    if (!target.expectedOutputs) {
      feedback.push("No expected voltages defined");
      return false;
    }
    const tolerance = target.tolerancePercent ?? 5;
    let allMatch = true;
    for (const [componentId, expectedVoltage] of Object.entries(target.expectedOutputs)) {
      const result = simulation.components.find((r) => r.componentId === componentId);
      if (!result) {
        feedback.push(`Component ${componentId} not found`);
        allMatch = false;
        continue;
      }
      if (typeof expectedVoltage === "number") {
        const percentDiff = Math.abs((result.voltage - expectedVoltage) / expectedVoltage) * 100;
        if (percentDiff > tolerance) {
          feedback.push(
            `Voltage mismatch: expected ${expectedVoltage}V, got ${result.voltage.toFixed(2)}V`
          );
          allMatch = false;
        }
      }
    }
    if (allMatch) {
      feedback.push("All voltage targets met!");
    }
    return allMatch;
  }
  validateCustomTarget(_simulation, _target, feedback) {
    feedback.push("Custom target validation not implemented");
    return true;
  }
};

// src/components/ComponentFactory.ts
import { generateId, COMPONENT_DEFAULTS } from "@circuit-crafter/shared";
function createTerminal(type, offsetX, offsetY, basePosition) {
  return {
    id: generateId(),
    type,
    position: {
      x: basePosition.x + offsetX,
      y: basePosition.y + offsetY
    },
    connectedTo: []
  };
}
function createBattery(position, voltage = 9) {
  const defaults = COMPONENT_DEFAULTS.battery;
  return {
    id: generateId(),
    type: "battery",
    position,
    rotation: 0,
    terminals: [
      createTerminal("positive", defaults.width, defaults.height / 2, position),
      createTerminal("negative", 0, defaults.height / 2, position)
    ],
    properties: { voltage }
  };
}
function createResistor(position, resistance = 1e3) {
  const defaults = COMPONENT_DEFAULTS.resistor;
  return {
    id: generateId(),
    type: "resistor",
    position,
    rotation: 0,
    terminals: [
      createTerminal("input", 0, defaults.height / 2, position),
      createTerminal("output", defaults.width, defaults.height / 2, position)
    ],
    properties: { resistance }
  };
}
function createLED(position, forwardVoltage = 2, maxCurrent = 0.02, color = "red") {
  const defaults = COMPONENT_DEFAULTS.led;
  return {
    id: generateId(),
    type: "led",
    position,
    rotation: 0,
    terminals: [
      createTerminal("positive", defaults.width / 2, 0, position),
      createTerminal("negative", defaults.width / 2, defaults.height, position)
    ],
    properties: { forwardVoltage, maxCurrent, color }
  };
}
function createSwitch(position, isOpen = true) {
  const defaults = COMPONENT_DEFAULTS.switch;
  return {
    id: generateId(),
    type: "switch",
    position,
    rotation: 0,
    terminals: [
      createTerminal("input", 0, defaults.height / 2, position),
      createTerminal("output", defaults.width, defaults.height / 2, position)
    ],
    properties: { isOpen }
  };
}
function createWire(fromPosition, toPosition) {
  return {
    id: generateId(),
    type: "wire",
    position: fromPosition,
    rotation: 0,
    terminals: [
      createTerminal("input", 0, 0, fromPosition),
      createTerminal("output", toPosition.x - fromPosition.x, toPosition.y - fromPosition.y, fromPosition)
    ],
    properties: {}
  };
}
function createANDGate(position) {
  const defaults = COMPONENT_DEFAULTS.and_gate;
  return {
    id: generateId(),
    type: "and_gate",
    position,
    rotation: 0,
    terminals: [
      createTerminal("input_a", 0, defaults.height / 4, position),
      createTerminal("input_b", 0, defaults.height * 3 / 4, position),
      createTerminal("output", defaults.width, defaults.height / 2, position)
    ],
    properties: {}
  };
}
function createORGate(position) {
  const defaults = COMPONENT_DEFAULTS.or_gate;
  return {
    id: generateId(),
    type: "or_gate",
    position,
    rotation: 0,
    terminals: [
      createTerminal("input_a", 0, defaults.height / 4, position),
      createTerminal("input_b", 0, defaults.height * 3 / 4, position),
      createTerminal("output", defaults.width, defaults.height / 2, position)
    ],
    properties: {}
  };
}
function createNOTGate(position) {
  const defaults = COMPONENT_DEFAULTS.not_gate;
  return {
    id: generateId(),
    type: "not_gate",
    position,
    rotation: 0,
    terminals: [
      createTerminal("input", 0, defaults.height / 2, position),
      createTerminal("output", defaults.width, defaults.height / 2, position)
    ],
    properties: {}
  };
}
function createGround(position) {
  const defaults = COMPONENT_DEFAULTS.ground;
  return {
    id: generateId(),
    type: "ground",
    position,
    rotation: 0,
    terminals: [createTerminal("input", defaults.width / 2, 0, position)],
    properties: {}
  };
}
function createCapacitor(position, capacitance = COMPONENT_DEFAULTS.capacitor.capacitance) {
  const defaults = COMPONENT_DEFAULTS.capacitor;
  return {
    id: generateId(),
    type: "capacitor",
    position,
    rotation: 0,
    terminals: [
      createTerminal("positive", 0, defaults.height / 2, position),
      createTerminal("negative", defaults.width, defaults.height / 2, position)
    ],
    properties: { capacitance }
  };
}
function createDiode(position, forwardVoltage = COMPONENT_DEFAULTS.diode.forwardVoltage) {
  const defaults = COMPONENT_DEFAULTS.diode;
  return {
    id: generateId(),
    type: "diode",
    position,
    rotation: 0,
    terminals: [
      createTerminal("anode", 0, defaults.height / 2, position),
      createTerminal("cathode", defaults.width, defaults.height / 2, position)
    ],
    properties: {
      forwardVoltage,
      reverseVoltage: COMPONENT_DEFAULTS.diode.reverseVoltage
    }
  };
}
function createTransistor(position, gain = COMPONENT_DEFAULTS.transistor.gain) {
  const defaults = COMPONENT_DEFAULTS.transistor;
  return {
    id: generateId(),
    type: "transistor",
    position,
    rotation: 0,
    terminals: [
      createTerminal("base", 0, defaults.height / 2, position),
      createTerminal("collector", defaults.width / 2, 0, position),
      createTerminal("emitter", defaults.width / 2, defaults.height, position)
    ],
    properties: { gain }
  };
}
function createBuzzer(position, frequency = COMPONENT_DEFAULTS.buzzer.frequency) {
  const defaults = COMPONENT_DEFAULTS.buzzer;
  return {
    id: generateId(),
    type: "buzzer",
    position,
    rotation: 0,
    terminals: [
      createTerminal("positive", defaults.width / 2, 0, position),
      createTerminal("negative", defaults.width / 2, defaults.height, position)
    ],
    properties: {
      frequency,
      forwardVoltage: COMPONENT_DEFAULTS.buzzer.forwardVoltage
    }
  };
}
function createMotor(position, ratedVoltage = COMPONENT_DEFAULTS.motor.ratedVoltage) {
  const defaults = COMPONENT_DEFAULTS.motor;
  return {
    id: generateId(),
    type: "motor",
    position,
    rotation: 0,
    terminals: [
      createTerminal("positive", defaults.width / 2, 0, position),
      createTerminal("negative", defaults.width / 2, defaults.height, position)
    ],
    properties: {
      ratedVoltage,
      ratedCurrent: COMPONENT_DEFAULTS.motor.ratedCurrent
    }
  };
}
function createPotentiometer(position, maxResistance = COMPONENT_DEFAULTS.potentiometer.maxResistance, initialPosition = 50) {
  const defaults = COMPONENT_DEFAULTS.potentiometer;
  return {
    id: generateId(),
    type: "potentiometer",
    position,
    rotation: 0,
    terminals: [
      createTerminal("input", 0, defaults.height / 2, position),
      createTerminal("wiper", defaults.width / 2, 0, position),
      createTerminal("output", defaults.width, defaults.height / 2, position)
    ],
    properties: {
      maxResistance,
      position: initialPosition
    }
  };
}
function createFuse(position, rating = COMPONENT_DEFAULTS.fuse.rating) {
  const defaults = COMPONENT_DEFAULTS.fuse;
  return {
    id: generateId(),
    type: "fuse",
    position,
    rotation: 0,
    terminals: [
      createTerminal("input", 0, defaults.height / 2, position),
      createTerminal("output", defaults.width, defaults.height / 2, position)
    ],
    properties: {
      rating,
      isBlown: false
    }
  };
}
function createComponent(type, position) {
  switch (type) {
    case "battery":
      return createBattery(position);
    case "resistor":
      return createResistor(position);
    case "led":
      return createLED(position);
    case "switch":
      return createSwitch(position);
    case "wire":
      return createWire(position, position);
    case "and_gate":
      return createANDGate(position);
    case "or_gate":
      return createORGate(position);
    case "not_gate":
      return createNOTGate(position);
    case "ground":
      return createGround(position);
    case "capacitor":
      return createCapacitor(position);
    case "diode":
      return createDiode(position);
    case "transistor":
      return createTransistor(position);
    case "buzzer":
      return createBuzzer(position);
    case "motor":
      return createMotor(position);
    case "potentiometer":
      return createPotentiometer(position);
    case "fuse":
      return createFuse(position);
    default:
      throw new Error(`Unknown component type: ${type}`);
  }
}
export {
  ChallengeValidator,
  CircuitGraph,
  CircuitSolver,
  createANDGate,
  createBattery,
  createBuzzer,
  createCapacitor,
  createComponent,
  createDiode,
  createFuse,
  createGround,
  createLED,
  createMotor,
  createNOTGate,
  createORGate,
  createPotentiometer,
  createResistor,
  createSwitch,
  createTransistor,
  createWire
};
